<script setup lang="ts">
    import { computed, ref } from 'vue';

    const props = withDefaults(defineProps<{
        options: string[];
        modelValue: string[];
        placeholder?: string;
        emptyText?: string;
        /**
         * Options are only searchable once the list is longer than this. Pass 0
         * for a list whose length varies, so the search box does not appear and
         * disappear as the list is narrowed by other means.
         */
        searchThreshold?: number;
    }>(), {
        placeholder: 'Search...',
        emptyText: 'Nothing to choose from',
        searchThreshold: 8
    });

    const emit = defineEmits<{
        'update:modelValue': [value: string[]];
    }>();

    const search = ref('');

    const showSearch = computed(() => props.options.length > props.searchThreshold);

    /**
     * Selected options are pinned to the top so that they stay visible and
     * removable even when the list is long or a search is narrowing it down.
     */
    const visibleOptions = computed<string[]>(() => {
        const term = search.value.trim().toLowerCase();
        const selected = props.modelValue.filter(o => props.options.includes(o));
        const unselected = props.options
            .filter(o => !props.modelValue.includes(o))
            .filter(o => term === '' || o.toLowerCase().includes(term));
        return [...selected, ...unselected];
    });

    function isSelected(option: string): boolean {
        return props.modelValue.includes(option);
    }

    function toggle(option: string): void {
        const next = isSelected(option)
            ? props.modelValue.filter(o => o !== option)
            : [...props.modelValue, option];
        emit('update:modelValue', next);
    }
</script>

<template>
    <div>
        <b-field
            v-if="showSearch || $slots['search-addon']"
            class="mb-2 search-row"
            grouped>
            <b-input
                v-if="showSearch"
                v-model="search"
                :placeholder="placeholder"
                type="search"
                icon="magnify"
                expanded />
            <slot name="search-addon" />
        </b-field>
        <div class="option-list">
            <p
                v-if="options.length === 0"
                class="has-text-grey is-size-6">
                {{ emptyText }}
            </p>
            <p
                v-else-if="visibleOptions.length === 0"
                class="has-text-grey is-size-6">
                No matches
            </p>
            <b-checkbox
                v-for="option in visibleOptions"
                :key="option"
                :model-value="isSelected(option)"
                :class="{ 'is-pinned': isSelected(option) }"
                class="option"
                @update:model-value="toggle(option)">
                <span
                    class="option-label"
                    :title="option">{{ option }}</span>
            </b-checkbox>
        </div>
    </div>
</template>

<style scoped>
  .option-list {
    max-height: 14rem;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .option {
    display: flex;
    width: 100%;
    margin-bottom: 0.15rem;
  }

  .option-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
</style>
